// lib/utils.ts

import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge, validators } from "tailwind-merge";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { CSSProperties } from "react";

/* ------------------------------------------------------------------ */
/*  UI / Tailwind                                                      */
/* ------------------------------------------------------------------ */

// Teach tailwind-merge about our custom font-size scale (defined in
// tailwind.config.ts under theme.extend.fontSize). Without this, classes
// like `text-body-sm` end up in the same group as `text-{color}` classes
// (e.g., `text-white`, `text-primary-foreground`), and twMerge silently
// drops one of them — which produced the infamous "black text on blue
// gradient buttons" bug.
//
// 🔴 Second écart, et il est de VERSION : `tailwind-merge` v3 est écrit pour
// Tailwind v4, où la classe nue `outline` est une ÉPAISSEUR (1 px). Ce dépôt
// tourne en Tailwind v3, où `outline` est le STYLE (trait plein). La librairie
// rangeait donc `outline` avec `outline-2` et supprimait le premier :
// `cn("outline outline-2 outline-brand-500")` rendait `outline-2 outline-brand-500`,
// soit `outline-style: none` — un liseré de couleur et d'épaisseur, mais INVISIBLE.
// Mesuré le 2026-10-15 dans l'éditeur d'e-mail : ni le bloc ni la colonne
// sélectionnés n'avaient le moindre cadre. On rend à `outline` son sens v3.
// Garde : lib/ui/class-merge.test.ts.
const twMerge = extendTailwindMerge({
  override: {
    classGroups: {
      "outline-w": [
        {
          outline: [
            validators.isNumber,
            validators.isArbitraryVariableLength,
            validators.isArbitraryLength,
          ],
        },
      ],
    },
  },
  extend: {
    classGroups: {
      "outline-style": ["outline"],
      "font-size": [
        "text-h1",
        "text-h2",
        "text-h3",
        "text-h4",
        "text-body",
        "text-body-sm",
        "text-caption",
        "text-label",
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const hasEnvVars = Boolean(
  process.env.NEXT_PUBLIC_SUPABASE_URL &&
    process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
);

/* ------------------------------------------------------------------ */
/*  Error handling                                                     */
/* ------------------------------------------------------------------ */

/**
 * Extracts a human-readable message from an unknown caught value.
 * Canonical replacement for `catch (e: any) { …e.message… }` — pair with
 * an un-annotated `catch (e)` (implicitly `unknown`) so we never reach for
 * `any` on error handling. Handles Error instances, Supabase/fetch error
 * shapes (`{ message }`), and falls back to a string cast.
 */
export function errMessage(err: unknown, fallback = "Erreur inconnue"): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return fallback;
}

/* ------------------------------------------------------------------ */
/*  Color picker utils                                                 */
/* ------------------------------------------------------------------ */

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function normalizeHexColor(value: string, fallback: string) {
  const v = String(value ?? "").trim();
  if (/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return v.toUpperCase();
  return String(fallback ?? "#FFFFFFFF").toUpperCase();
}

function toHexByte(n: number) {
  return clamp(Math.round(n), 0, 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
}

export function rgbaToHex8(r: number, g: number, b: number, a: number) {
  return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}${toHexByte(a * 255)}`;
}

export function parseHexColor(value: string) {
  const hex = normalizeHexColor(value, "#FFFFFFFF").slice(1);

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;

  return { r, g, b, a };
}

export function rgbToHsv(r: number, g: number, b: number) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (d !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
        break;
      case gn:
        h = ((bn - rn) / d + 2) * 60;
        break;
      case bn:
        h = ((rn - gn) / d + 4) * 60;
        break;
    }
  }

  return { h, s, v };
}

export function hsvToRgb(h: number, s: number, v: number) {
  const c = v * s;
  const hh = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (hh >= 0 && hh < 1) [r1, g1, b1] = [c, x, 0];
  else if (hh >= 1 && hh < 2) [r1, g1, b1] = [x, c, 0];
  else if (hh >= 2 && hh < 3) [r1, g1, b1] = [0, c, x];
  else if (hh >= 3 && hh < 4) [r1, g1, b1] = [0, x, c];
  else if (hh >= 4 && hh < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  const m = v - c;

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

export const checkerboardBackgroundStyle: CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg, #E5E7EB 25%, transparent 25%), linear-gradient(-45deg, #E5E7EB 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #E5E7EB 75%), linear-gradient(-45deg, transparent 75%, #E5E7EB 75%)",
  backgroundSize: "12px 12px",
  backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0px",
};

/* ------------------------------------------------------------------ */
/*  Dates                                                              */
/* ------------------------------------------------------------------ */

/**
 * Parse a value into an ISO timestamp string, or `null` if the input is
 * missing/invalid.
 *
 * Returning `null` (instead of falling back to `new Date()`) is critical for
 * sort keys and cursor timestamps — a "now" fallback makes rows with missing
 * data jump to the top of the list on every render. Callers that genuinely
 * want a fallback should write it explicitly:
 *
 *   safeIso(x) ?? ""                          // sort-safe (defaults to oldest)
 *   safeIso(x) ?? new Date().toISOString()    // display-time fallback
 */
export function safeIso(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string") {
    let s = v.trim();
    if (!s) return null;
    if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T");
    s = s.replace(/([+-]\d{2})$/, "$1:00");
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  const d = new Date(v as string | number | Date);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/* ------------------------------------------------------------------ */
/*  Background video runtime (website)                                 */
/* ------------------------------------------------------------------ */

export const OC_BGVIDEO_RUNTIME = `<script>(function(){try{var d=document;function ensureCss(){if(d.getElementById('oc-bgvideo-live-css'))return;var s=d.createElement('style');s.id='oc-bgvideo-live-css';s.textContent='.oc-bgvideo-host{position:relative;isolation:isolate}.oc-bgvideo-live{position:absolute;inset:0;overflow:hidden;z-index:-1;pointer-events:none}.oc-bgvideo-live>video{position:absolute;min-width:100%;min-height:100%;top:50%;left:50%;transform:translate(-50%,-50%);object-fit:cover}.oc-bgvideo-host>:not(.oc-bgvideo-live){position:relative;z-index:1}[data-oc-bgvideo]{background-color:transparent!important}';d.head&&d.head.appendChild(s)}function mount(el,url){if(!url)return;ensureCss();el.classList.add('oc-bgvideo-host');var w=el.querySelector(':scope > .oc-bgvideo-live');if(!w){w=d.createElement('div');w.className='oc-bgvideo-live';var f=el.firstChild;f?el.insertBefore(w,f):el.appendChild(w)}var v=w.querySelector('video');if(!v){v=d.createElement('video');v.setAttribute('muted','');v.muted=true;v.setAttribute('playsinline','');v.setAttribute('autoplay','');v.setAttribute('loop','');v.preload='auto';w.appendChild(v)}if(v.src!==url){v.src=url;try{v.load()}catch(e){}var play=function(){v.play&&v.play().catch(function(){})};'requestIdleCallback'in window?requestIdleCallback(play):setTimeout(play,0)}}function boot(){d.querySelectorAll('[data-oc-bgvideo]').forEach(function(el){var url=(el.getAttribute('data-oc-bgvideo')||'').trim();if(url)mount(el,url)})}(d.readyState==='loading')?d.addEventListener('DOMContentLoaded',boot,{once:true}):boot()}catch(e){}})();</script>`;

export function appendOcBgVideoRuntime(srcHtml: string): string {
  if (!srcHtml) return srcHtml;
  if (/oc-bgvideo-live-css|oc-bgvideo-live>video/.test(srcHtml)) return srcHtml;
  const re = /<\/body\s*>/i;
  return re.test(srcHtml)
    ? srcHtml.replace(re, OC_BGVIDEO_RUNTIME + "</body>")
    : srcHtml + OC_BGVIDEO_RUNTIME;
}

export function normalizePath(p?: string) {
  let s = (p || "/").trim();
  if (!s.startsWith("/")) s = "/" + s;
  if (s.length > 1) s = s.replace(/\/+$/, "");
  return s || "/";
}

export function slugifySegment(s: string) {
  return (
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .replace(/-{2,}/g, "-") || "page"
  );
}

export function singlePath(raw: string) {
  const s = String(raw || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .replace(/-{2,}/g, "-");

  return s ? `/${s}` : "/";
}

// `getFirstSiteIdForOrg` vivait ici. La question « quel est le site de l'org ? »
// est partie dans `lib/website/sites/primary-site.ts` : ce fichier porte `cn()` et se retrouve
// importé par des dizaines de composants CLIENT, donc il ne peut pas être marqué
// `server-only` — et le prédicat « site principal de l'org » doit l'être, pour que
// personne ne le recopie sans son filtre `demo_slug IS NULL`.

export async function loadProjectForSiteAndPath(
  supabase: SupabaseClient,
  siteId: string,
  rawPath: string,
): Promise<unknown | null> {
  void rawPath;

  const { data: current, error: currentErr } = await supabase
    .from("site_versions")
    .select("project_json")
    .eq("site_id", siteId)
    .eq("status", "draft")
    .eq("is_current", true)
    .not("project_json", "is", null)
    .maybeSingle();

  if (currentErr) {
    console.error("[loadProjectForSiteAndPath] current draft error:", currentErr);
  }

  if (current?.project_json) {
    return current.project_json;
  }

  const { data: last, error: lastErr } = await supabase
    .from("site_versions")
    .select("project_json")
    .eq("site_id", siteId)
    .not("project_json", "is", null)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (lastErr) {
    console.error("[loadProjectForSiteAndPath] last version error:", lastErr);
  }

  return last?.project_json ?? null;
}

/* ------------------------------------------------------------------ */
/*  Custom field helpers (forms)                                       */
/* ------------------------------------------------------------------ */

import type {
  ContactCustomFieldRow,
  CreateCustomFieldInput,
  UiCustomFieldKind,
} from "@/app/(protected)/(settings)/settings/custom-fields/types";
import {
  KEY_MAX_LEN as CUSTOM_FIELD_KEY_MAX_LEN,
  UI_FIELD_KIND_LABELS,
  displayKindFromRow,
  slugifyKey as slugifyCustomFieldKey,
} from "@/app/(protected)/(settings)/settings/custom-fields/types";
import type { FieldTemplate } from "@/app/(protected)/(fullscreen)/website/forms/[id]/types";

export function uiKindFromRow(row: ContactCustomFieldRow): UiCustomFieldKind {
  const forced = (row as { __uiKind?: UiCustomFieldKind }).__uiKind;
  if (forced) return forced;

  const ui = (row as { options?: { ui?: unknown } }).options?.ui;

  if (typeof ui === "string" && ui in UI_FIELD_KIND_LABELS) {
    return ui as UiCustomFieldKind;
  }

  if (ui && typeof ui === "object" && "kind" in ui) {
    const k = (ui as { kind: unknown }).kind;
    if (typeof k === "string" && k in UI_FIELD_KIND_LABELS) {
      return k as UiCustomFieldKind;
    }
  }

  return displayKindFromRow(row) as UiCustomFieldKind;
}

export function canonicalCustomFieldKey(label: string): string {
  return slugifyCustomFieldKey(label)
    .slice(0, CUSTOM_FIELD_KEY_MAX_LEN)
    .replace(/_+$/g, "");
}

/**
 * Build a `CreateCustomFieldInput` with a label that won't collide with the
 * org's existing custom fields. List-kinds (select / multi-select / radio) get
 * two starter options the user can rename. Used by the form editor when the
 * user clicks "+ Nouveau champ" — the field is then created in DB immediately
 * (no client-side draft phase).
 */
export function buildDefaultCreateInputForKind(
  kind: UiCustomFieldKind,
  existingLabelsLower: Set<string>,
): CreateCustomFieldInput {
  const base = UI_FIELD_KIND_LABELS[kind] ?? "Information personnalisée";

  let label = base;
  for (let i = 2; i <= 200; i++) {
    if (!existingLabelsLower.has(label.trim().toLowerCase())) break;
    label = `${base} ${i}`;
  }
  if (existingLabelsLower.has(label.trim().toLowerCase())) {
    // ridiculously many duplicates — fall back to a timestamp tag
    label = `${base} ${Date.now()}`;
  }

  const input: CreateCustomFieldInput = { label, kind };
  if (
    kind === "select_single" ||
    kind === "select_multiple" ||
    kind === "radio"
  ) {
    input.optionValues = ["Option 1", "Option 2"];
  }
  return input;
}

/**
 * Extract option labels from a custom field row. The DB stores the canonical
 * `{id, label}[]` shape; any entries that don't match are dropped.
 *
 * Used by the form builder to populate `FormField.options` for preview and
 * snapshot purposes. The public form render does its own live lookup.
 */
export function getRowOptionValues(row: ContactCustomFieldRow): string[] {
  const opt = (row.options ?? {}) as { values?: unknown };
  const values = opt.values;
  if (!Array.isArray(values)) return [];
  return values
    .map((v: unknown) => {
      if (
        v &&
        typeof v === "object" &&
        "label" in v &&
        typeof (v as { label?: unknown }).label === "string"
      ) {
        return (v as { label: string }).label;
      }
      return "";
    })
    .filter((s: string) => s.length > 0);
}

export function templateFromCustomFieldRow(
  row: ContactCustomFieldRow,
): FieldTemplate {
  const kind = uiKindFromRow(row);
  const label = row.label || "Information personnalisée";

  const meta = {
    customFieldId: row.id,
    customFieldKey: row.key,
    customFieldUiKind: kind,
  };

  switch (kind) {
    case "single_line":
      return { type: "text", label, placeholder: "Je cherche un professionnel pour...", ...meta };
    case "multi_line":
      return { type: "textarea", label, placeholder: "Ex : Votre message…", ...meta };
    case "phone":
      return { type: "phone", label, placeholder: "01 23 45 67 89", ...meta };
    case "datetime":
      return { type: "date", label, placeholder: "31/12/2026", ...meta };
    case "select_single":
    case "select_multiple":
    case "radio": {
      const options = getRowOptionValues(row);
      return {
        type: "select",
        label,
        options: options.length ? options : ["Option 1", "Option 2"],
        selectVariant: kind === "radio" ? "radio" : "select",
        multiple: kind === "select_multiple",
        ...meta,
      };
    }
    case "checkbox":
      return { type: "checkbox", label, required: false, ...meta };
    case "currency":
      return { type: "text", label, placeholder: "99,90", ...meta };
    case "number":
      return { type: "text", label, placeholder: "42", ...meta };
    case "document":
    case "signature":
    default:
      return { type: "text", label, placeholder: "Ex : …", ...meta };
  }
}